import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@mdi/react';
import { mdiChevronDown } from '@mdi/js';
import HeroGlobe from './HeroGlobe';
import { MAP_ZOOM_LEVELS } from '../constants/mapConstants';

interface InteractivePhoneFrameProps {
  isMobile: boolean;
}

const InteractivePhoneFrame: React.FC<InteractivePhoneFrameProps> = ({
  isMobile,
}) => {
  const [zoomLevel, setZoomLevel] = useState(0);
  const [isPostSelected, setIsPostSelected] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showSubtext, setShowSubtext] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  const [phoneFrameLoaded, setPhoneFrameLoaded] = useState(false);
  const [loopingTextIndex, setLoopingTextIndex] = useState(0);
  const [loopingTextVisible, setLoopingTextVisible] = useState(true);
  const [phoneFrameVisible, setPhoneFrameVisible] = useState(false);
  const [heroTextLeftPosition, setHeroTextLeftPosition] = useState<number | null>(null);
  const [heroTextRightPosition, setHeroTextRightPosition] = useState<number | null>(null);
  const [heroTextTopPosition, setHeroTextTopPosition] = useState<number | null>(null);
  const [heroTextBottomPosition, setHeroTextBottomPosition] = useState<number | null>(null);

  const loopingTexts = ["Real people. Real places.", "Live. Unedited. Unfiltered.", "Share your story where it happens."];

  // Fade in phone frame on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setPhoneFrameVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Memoize the layout callback to prevent unnecessary re-renders
  const handleGlobeLayout = useCallback((left: number, right: number, top: number, bottom: number) => {
    setHeroTextLeftPosition(left);
    setHeroTextRightPosition(right);
    setHeroTextTopPosition(top);
    setHeroTextBottomPosition(bottom);
  }, []);

  // Inject bounce animation keyframes
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes bounce {
        0%, 20%, 50%, 80%, 100% {
          transform: translateY(0);
        }
        40% {
          transform: translateY(-10px);
        }
        60% {
          transform: translateY(-5px);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (style.parentNode) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Fade in animations - only after intro completes
  useEffect(() => {
    if (!introComplete) return;

    // Fade in title first
    const titleTimer = setTimeout(() => {
      setShowTitle(true);
    }, 500);

    // Then fade in subtext after a dramatic pause
    const subtextTimer = setTimeout(() => {
      setShowSubtext(true);
    }, 1500);

    return () => {
      clearTimeout(titleTimer);
      clearTimeout(subtextTimer);
    };
  }, [introComplete]);

  // Looping text animation - only after intro completes
  useEffect(() => {
    if (!introComplete || !phoneFrameLoaded) return;

    // Show current text for 3 seconds
    const showTimer = setTimeout(() => {
      setLoopingTextVisible(false);
    }, 3000);

    // Switch to next text after fade out (3s + 300ms fade)
    const switchTimer = setTimeout(() => {
      setLoopingTextIndex((prev) => (prev + 1) % loopingTexts.length);
      setLoopingTextVisible(true);
    }, 3300);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(switchTimer);
    };
  }, [introComplete, phoneFrameLoaded, loopingTextIndex, loopingTexts.length]);

  const handleScrollToNext = () => {
    const firstPersonaSection = document.querySelector('[data-persona-section]');
    if (firstPersonaSection) {
      firstPersonaSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const styles = {
    heroSection: {
      position: 'relative' as const,
      width: '100%',
      minHeight: isMobile ? '60vh' : '90vh',
      overflow: 'hidden',
      paddingTop: isMobile ? '56px' : '72px',
      display: 'flex',
      flexDirection: isMobile ? 'column' as const : 'row' as const,
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0',
      padding: isMobile ? '56px 0 40px 0' : '72px 0 40px 0',
    },

    abovePhoneContainer: {
      display: isMobile ? 'flex' : 'none',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '12px 20px',
      width: '100%',
    },

    belowPhoneContainer: {
      display: isMobile ? 'flex' : 'none',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      width: '100%',
      gap: '16px',
    },

    phoneFrameContainer: {
      position: 'relative' as const,
      height: isMobile ? '80vh' : '85vh',
      aspectRatio: '1297 / 2674',
      borderRadius: '19.5%',
      background: 'transparent',
      opacity: phoneFrameVisible ? 1 : 0,
      transition: 'opacity 0.8s ease-in-out',
    },

    heroContent: {
      position: 'absolute' as const,
      top: 'calc(50% + 10px)',
      left: heroTextLeftPosition !== null ? `${heroTextLeftPosition}px` : '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 10,
      textAlign: 'center' as const,
      width: 'auto',
      maxWidth: '400px',
      padding: '0 20px',
      transition: 'left 0.3s ease, top 0.3s ease',
      visibility: (heroTextLeftPosition !== null ? 'visible' : 'hidden') as 'visible' | 'hidden',
    },

    heroTitle: {
      fontSize: isMobile ? '1.75rem' : '2.0rem',
      fontWeight: '700',
      lineHeight: '1.2',
      margin: '0',
      padding: 0,
      color: '#fc2c03',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      opacity: showTitle ? 1 : 0,
      transition: 'opacity 2s ease-in-out',
      textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    },

    globeContainer: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 1,
      borderRadius: '19.5%',
      overflow: 'hidden',
      opacity: phoneFrameLoaded ? 1 : 0,
      transition: 'opacity 0.8s ease-in-out',
    },

    phoneFrame: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 2,
      pointerEvents: 'none' as const,
      display: 'block',
    },

    globeSubtextContainer: {
      position: 'absolute' as const,
      top: 'calc(50% + 10px)',
      bottom: 'auto',
      left: 'auto',
      right: heroTextRightPosition !== null ? `${heroTextRightPosition}px` : '50%',
      transform: 'translate(50%, -50%)',
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      gap: '16px',
      width: 'auto',
      maxWidth: '400px',
      transition: 'right 0.3s ease, bottom 0.3s ease',
      visibility: (heroTextRightPosition !== null ? 'visible' : 'hidden') as 'visible' | 'hidden',
    },

    globeSubtext: {
      fontSize: '1.5rem',
      fontWeight: '700',
      lineHeight: '1.2',
      color: '#fc2c03',
      margin: '0',
      padding: '0',
      textAlign: 'center' as const,
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      opacity: showSubtext ? 1 : 0,
      transition: 'opacity 2s ease-in-out',
      textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    },

    scrollIndicator: {
      position: 'absolute' as const,
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 10,
      opacity: 0.7,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'opacity 0.3s ease',
    },

    tapIndicatorContainer: {
      position: 'absolute' as const,
      bottom: '18vh',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 10,
      opacity: 1,
      transition: 'opacity 0.3s ease',
      pointerEvents: 'none' as const,
    },

    tapIndicator: {
      fontSize: '1rem',
      color: '#fc2c03',
      fontWeight: '600',
      whiteSpace: 'nowrap' as const,
    },

    chevronIcon: {
      animation: 'bounce 2s infinite',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    loopingTextContainer: {
      position: 'absolute' as const,
      top: isMobile ? '10vh' : 'calc(7.5vh + 1vw)',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 10,
      opacity: loopingTextVisible ? 1 : 0,
      transition: 'opacity 0.3s ease',
      width: '80%',
      maxWidth: '300px',
    },

    loopingText: {
      fontSize: isMobile ? '1.4rem' : 'clamp(1rem, 1.75vw, 1.75rem)',
      fontWeight: '700',
      color: '#fc2c03',
      textAlign: 'center' as const,
      margin: 0,
      lineHeight: '1.3',
      whiteSpace: 'pre-line' as const,
    },

    getStartedBtn: {
      display: 'inline-block',
      padding: '12px 28px',
      background: 'linear-gradient(to bottom, #fc2c03 0%, #ff6b4a 100%)',
      border: 'none',
      borderRadius: '8px',
      textDecoration: 'none',
      color: 'white',
      fontWeight: '700',
      fontSize: '1rem',
      boxShadow: '0 4px 20px rgba(252, 44, 3, 0.3)',
      opacity: showSubtext ? 1 : 0,
      transition: 'opacity 2s ease-in-out, transform 0.2s ease',
      cursor: 'pointer',
    },
  };

  return (
    <div style={styles.heroSection}>

      {/* Phone frame container with fixed aspect ratio */}
      <div style={styles.phoneFrameContainer}>
        {/* Globe container - confined to phone screen area */}
        {phoneFrameLoaded && (
          <div style={styles.globeContainer}>
            <HeroGlobe
              isMobile={false}
              onZoomChange={setZoomLevel}
              onPostSelect={setIsPostSelected}
              onGlobeLayout={handleGlobeLayout}
              isPhoneFrameMode={true}
              onIntroComplete={() => setIntroComplete(true)}
            />
          </div>
        )}

        {/* iPhone frame */}
        <img
          src="https://static.storm-byte.dev/apple-iphone-15-black-portrait_4.png"
          alt="iPhone Frame"
          style={styles.phoneFrame}
          onLoad={() => setPhoneFrameLoaded(true)}
        />

        {/* Looping text in top right */}
        {phoneFrameLoaded && introComplete && zoomLevel <= (isMobile ? MAP_ZOOM_LEVELS.TEXT_VISIBLE_THRESHOLD_MOBILE : MAP_ZOOM_LEVELS.TEXT_VISIBLE_THRESHOLD_DESKTOP) && !isPostSelected && (
          <div style={styles.loopingTextContainer}>
            <p style={styles.loopingText}>{loopingTexts[loopingTextIndex]}</p>
          </div>
        )}

        {/* Tap indicator */}
        {phoneFrameLoaded && introComplete && zoomLevel <= (isMobile ? MAP_ZOOM_LEVELS.TEXT_VISIBLE_THRESHOLD_MOBILE : MAP_ZOOM_LEVELS.TEXT_VISIBLE_THRESHOLD_DESKTOP) && !isPostSelected && (
          <div style={styles.tapIndicatorContainer}>
            <span style={styles.tapIndicator}>Tap a post</span>
          </div>
        )}

        {/* Scroll indicator inside phone frame */}
        {phoneFrameLoaded && introComplete && zoomLevel <= 2 && !isPostSelected && (
          <div
            style={styles.scrollIndicator}
            onClick={handleScrollToNext}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.7';
            }}
            aria-label="Scroll to next section"
          >
            <div style={styles.chevronIcon}>
              <Icon path={mdiChevronDown} size={2} color="#dc2626" />
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default InteractivePhoneFrame;
